// Everything from OpenLayers
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Point from "ol/geom/Point";
import { deserialize } from "flatgeobuf/lib/mjs/ol";
import { bbox } from "ol/loadingstrategy";
import { setSourceGeometry } from "../util/featureWkt.mjs";
import {
  FGB_PREFIX_LEN,
  fgbHeaderRange,
  parseFgbFeaturesCount,
  parseFgbHeaderLength,
} from "./fgbHeader.mjs";

// Some parameters used to render the dial and bearing used to show the direction of the sign.

// Global variables used to abort API calls because the user has changed the map.
// let abortController, signal;

/**
 * @param {VectorFeatureControl} vectorFeatureControl
 * @param {string[]} urls
 * @param {function} getStyleNormal
 * @param {function} transformFeature
 * @param {number} minZoom
 * @param {string} dataProjection
 * @returns
 */
export function getFGBLayer(
  vectorFeatureControl,
  urls,
  getStyleNormal,
  transformFeature,
  minZoom = 17,
  addFeatureHandlers = [],
  dataProjection = "EPSG:4326",
) {
  const vectorSource = new VectorSource({
    minZoom: minZoom,
    strategy: bbox,

    loader: function (extent, resolution, projection, success, failure) {
      // Use flatgeobuf JavaScript API to iterate features as geojson.
      // Because we specify a bounding box, flatgeobuf will only fetch the relevant subset of data,
      // rather than the entire file.
      if (extent[0] == -Infinity) {
        return;
      }
      async function populate() {
        const rect = fgbBoundingBox(extent, projection.getCode());
        for (const url of urls) {
          try {
            // Pre-flight: validate the header before handing the URL to the
            // flatgeobuf reader. A malformed/truncated/empty file whose header
            // parses to featuresCount <= 0 (or NaN) makes the library's
            // synchronous `calcTreeSize` loop forever on the main thread,
            // freezing the whole tab — no error is thrown and the catch below
            // never runs. Validating here turns that hang into a normal error.
            await validateFgbHeader(url);
            let iter = deserialize(url, { rect });
            const features = [];
            for await (let feature of iter) {
              // Keep the file's own coordinates for the WKT row before reprojecting.
              const sourceGeometry = feature.getGeometry().clone();
              feature
                .getGeometry()
                .transform(dataProjection, projection.getCode());
              feature.setProperties(
                flattenJsonProperties(feature.getProperties()),
              );
              const transformed = transformFeature(feature);
              setSourceGeometry(transformed, sourceGeometry);
              features.push(transformed);
            }
            success(features);
            vectorSource.addFeatures(features);
          } catch (err) {
            if (err.message != "Not a FlatGeobuf file") {
              console.error("Unable to fetch data for url " + url, err);
            }
            vectorFeatureControl.set(
              "loadErrorReason",
              err.message ?? "Unknown error",
            );
            failure();
          }
        }
      }
      populate();
    },
  });

  addFeatureHandlers.forEach((handler) => {
    vectorSource.on("featuresloadend", handler);
  });

  vectorSource.on("featuresloadstart", () => {
    vectorFeatureControl.set("loading", true);
    vectorFeatureControl.set("loadError", false);
    vectorFeatureControl.set("loadErrorReason", null);
  });
  vectorSource.on("featuresloadend", () => {
    vectorFeatureControl.set("loading", false);
  });
  vectorSource.on("featuresloaderror", () => {
    vectorFeatureControl.set("loading", false);
    vectorFeatureControl.set("loadError", true);
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    minZoom: minZoom,
    zIndex: 8,
    visible: false,
    style: function (feature) {
      return getStyleNormal(feature);
    },
  });

  vectorFeatureControl.set("enabled", true);
  vectorFeatureControl.on("propertychange", function (e) {
    if (e.key !== "loading" && e.key !== "tooLarge" && e.key !== "loadError") {
      propertyChangeHandler(vectorFeatureControl, vectorLayer);
    }
    if (e.key === "zoom") {
      const zoom = e.target.get("zoom");
      if (
        zoom >= vectorLayer.getMinZoom() &&
        zoom <= vectorLayer.getMaxZoom()
      ) {
        vectorFeatureControl.set("active", true);
      } else {
        vectorFeatureControl.set("active", false);
      }
    }
  });

  return vectorLayer;
}

/**
 * Fetches and validates just the FlatGeobuf header via HTTP Range requests,
 * mirroring the checks the library's `HttpReader.open` performs — plus the one
 * it is missing: a finite, positive `featuresCount`. The library feeds the
 * parsed count straight into `calcTreeSize`, whose `do … while (1 !== l)` loop
 * never terminates for 0/NaN/negative counts, hanging the main thread.
 *
 * The byte offsets live in `fgbHeader.mjs`: flatgeobuf 4 parses the header
 * size-prefixed, so the header request has to start at the length prefix.
 *
 * Throws a descriptive Error (caught by the loader) instead of freezing.
 *
 * @param {string} url
 */
export async function validateFgbHeader(url) {
  const getRange = async (start, length) => {
    const res = await fetch(url, {
      headers: { Range: `bytes=${start}-${start + length - 1}` },
    });
    if (!res.ok && res.status !== 206) {
      throw new Error(`HTTP ${res.status} fetching FlatGeobuf header`);
    }
    return new Uint8Array(await res.arrayBuffer());
  };

  const headerLength = parseFgbHeaderLength(await getRange(0, FGB_PREFIX_LEN));

  const { start, length } = fgbHeaderRange(headerLength);
  const headerBytes = await getRange(start, length);
  if (headerBytes.byteLength < length) {
    throw new Error("Truncated FlatGeobuf header");
  }

  const featuresCount = parseFgbFeaturesCount(headerBytes);
  if (!Number.isFinite(featuresCount) || featuresCount <= 0) {
    throw new Error(
      `Invalid FlatGeobuf: featuresCount=${featuresCount} (file empty or corrupt)`,
    );
  }
}

function fgbBoundingBox(extent, projectionCode) {
  const minPoint = new Point([extent[0], extent[1]]);
  minPoint.transform(projectionCode, "EPSG:4326");

  const maxPoint = new Point([extent[2], extent[3]]);
  maxPoint.transform(projectionCode, "EPSG:4326");

  return {
    minX: minPoint.getCoordinates()[0],
    minY: minPoint.getCoordinates()[1],
    maxX: maxPoint.getCoordinates()[0],
    maxY: maxPoint.getCoordinates()[1],
  };
}

function flattenJsonProperties(properties) {
  const newEntries = Object.entries(properties).map((entry) => {
    try {
      return [entry[0], JSON.parse(entry[1])];
    } catch {
      return entry;
    }
  });
  return Object.fromEntries(newEntries);
}

/**
 * @param {VectorFeatureControl} vectorFeatureControl
 * @param {VectorLayer} vectorLayer
 */
function propertyChangeHandler(vectorFeatureControl, vectorLayer) {
  if (vectorFeatureControl.get("vectorToggle") === true) {
    vectorLayer.setVisible(true);
  } else {
    // When vectorToggle is not set, the checkbox is not checked, and so the layer is made invisible.
    vectorLayer.setVisible(false);
  }
}
